<!-- Tabla de estimación -->

<template>
  <div class="space-y-6">
    <h2 class="text-slate-900 dark:text-slate-100 text-lg font-semibold mb-4">Tabla de estimación</h2>
    
    <!-- 1️⃣ Regular Items Section -->
    <div v-if="regularItems.length > 0" class="space-y-3">
      <h3 class="text-slate-700 dark:text-slate-300 text-sm font-medium">Items</h3>
      <draggable 
        v-model="draggableRegularItems" 
        tag="div" 
        class="space-y-2"
        item-key="regular-item"
        handle=".drag-handle"
        ghost-class="ghost"
      >
        <template #item="{element: item}">
          <div class="flex items-center justify-between py-2 px-3 rounded-md bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors">
          
          <!-- Service Name -->
          <div class="flex-1 flex items-center gap-2">
            <span class="drag-handle cursor-move text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300">
              <i class="material-icons-round">drag_indicator</i>
            </span>
            <!-- Team Tag -->
            <span v-if="item.service.team" 
                  :class="getTeamTagClass(item.service.team)"
                  class="team-tag inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium">
              {{ item.service.team }}
            </span>
            <p class="text-slate-800 dark:text-slate-200 text-sm font-medium">{{ item.service.name }}</p>
          </div>

          <!-- Controls Section -->
          <div class="flex items-center gap-4 min-w-[320px]">
            <!-- Quantity Input -->
            <div class="flex items-center gap-2">
              <span class="text-slate-600 dark:text-slate-400 text-sm">Cantidad:</span>
              <input
                type="number"
                v-model.number="item.quantity"
                @input="updateItemWithValidation($event, getOriginalIndex(item))"
                min="1"
                step="1"
                class="form-control form-control-sm dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 item-quantity-input"
                aria-label="Item quantity"
              />
            </div>

            <!-- Hours Display -->
            <div class="flex items-center gap-2 min-w-[140px]">
              <span class="text-slate-600 dark:text-slate-400 text-sm">Horas:</span>
              <div class="flex items-center gap-1">
                <input
                  type="number"
                  v-model.number="item.service.hours"
                  @input="updateItemHours(getOriginalIndex(item))"
                  min="0.1"
                  step="0.1"
                  class="form-control form-control-sm dark:bg-slate-700 dark:border-slate-600 dark:text-slate-100 item-hours-input"
                  aria-label="Item hours"
                  :title="'Default: ' + item.service.defaultHours"
                />
                <button 
                  v-if="item.service.hours !== item.service.defaultHours"
                  @click="resetHours(getOriginalIndex(item))"
                  class="btn btn-icon text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300"
                  title="Restaurar horas predeterminadas"
                >
                  <i class="material-icons">restart_alt</i>
                </button>
              </div>
            </div>

            <!-- Supervision Toggle -->
            <div class="flex items-center gap-2">
              <button 
                @click="toggleSupervisionOnly(getOriginalIndex(item))"
                class="btn btn-sm bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-600 transition-colors"
                title="Marcar como solo supervisión"
              >
                <span class="w-1.5 h-1.5 rounded-full mr-1 bg-slate-400"></span>
                <span class="hidden sm:inline text-xs">Solo Supervisión</span>
              </button>
            </div>

            <!-- Remove Button -->
            <div class="flex items-center gap-2">
              <button @click="confirmRemove(getOriginalIndex(item))" 
                      class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium hover:underline focus:outline-none flex items-center gap-1"
                      :title="'Remove ' + item.service.name">
                <i class="material-icons-round text-sm">delete</i>
                <span>Eliminar</span>
              </button>
            </div>
          </div>
          </div>
        </template>
      </draggable>
    </div>

    <!-- 2️⃣ Subtotal de Items -->
    <div class="flex items-center justify-between py-2 px-3 rounded-md bg-slate-50 dark:bg-slate-700">
      <p class="text-slate-700 dark:text-slate-300 text-sm font-medium">Subtotal de Items</p>
      <p class="text-slate-700 dark:text-slate-300 text-sm font-medium">{{ baseHours }} horas</p>
    </div>

    <!-- 3️⃣ Subtotal de Supervisión (calculated supervision hours from regular items - greyed out) -->
    <div v-if="creativeSupervisionHours > 0" 
         class="flex items-center justify-between py-2 px-3 rounded-md bg-slate-100 dark:bg-slate-600 opacity-75">
      <p class="text-slate-600 dark:text-slate-400 text-sm font-medium italic">Subtotal de Supervisión (calculada)</p>
      <p class="text-slate-600 dark:text-slate-400 text-sm font-medium">{{ creativeSupervisionHours }} horas</p>
    </div>

    <!-- 4️⃣ Supervision-only Items Section -->
    <div v-if="supervisionOnlyItems.length > 0" class="space-y-3">
      <h3 class="text-orange-700 dark:text-orange-300 text-sm font-medium flex items-center gap-2">
        <i class="material-icons text-sm">visibility</i>
        Items Solo Supervisión
      </h3>
      <draggable
        v-model="draggableSupervisionOnlyItems"
        tag="div"
        class="space-y-2 p-3 rounded-lg border-2 border-orange-200 dark:border-orange-700 bg-orange-50/30 dark:bg-orange-900/10"
        item-key="supervision-item"
        handle=".drag-handle"
        ghost-class="ghost"
      >
        <template #item="{element: item}">
          <div class="flex items-center justify-between py-2 px-3 rounded-md bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 hover:bg-orange-100 dark:hover:bg-orange-800/30 transition-colors">
            
            <!-- Service Name with Badge -->
            <div class="flex-1 flex items-center gap-2">
              <span class="drag-handle cursor-move text-orange-400 dark:text-orange-500 hover:text-orange-600 dark:hover:text-orange-300">
                <i class="material-icons-round">drag_indicator</i>
              </span>
              <!-- Team Tag -->
              <span v-if="item.service.team" 
                    :class="getTeamTagClass(item.service.team)"
                    class="team-tag inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium">
                {{ item.service.team }}
              </span>
              <p class="text-orange-800 dark:text-orange-200 text-sm font-medium">{{ item.service.name }}</p>
              <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 dark:bg-orange-800/50 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-600">
                <i class="material-icons text-xs">visibility</i>
                Supervisión
              </span>
            </div>

            <!-- Controls Section -->
            <div class="flex items-center gap-4 min-w-[320px]">
              <!-- Quantity Input -->
              <div class="flex items-center gap-2">
                <span class="text-orange-600 dark:text-orange-400 text-sm">Cantidad:</span>
                <input
                  type="number"
                  v-model.number="item.quantity"
                  @input="updateItemWithValidation($event, getOriginalIndex(item))"
                  min="1"
                  step="1"
                  class="form-control form-control-sm bg-orange-50 dark:bg-orange-900/30 border-orange-300 dark:border-orange-600 text-orange-900 dark:text-orange-100 item-quantity-input"
                  aria-label="Item quantity"
                />
              </div>

              <!-- Hours Display -->
              <div class="flex items-center gap-2 min-w-[140px]">
                <span class="text-orange-600 dark:text-orange-400 text-sm">Horas:</span>
                <div class="flex items-center gap-1">
                  <input
                    type="number"
                    v-model.number="item.service.hours"
                    @input="updateItemHours(getOriginalIndex(item))"
                    min="0.1"
                    step="0.1"
                    class="form-control form-control-sm bg-orange-50 dark:bg-orange-900/30 border-orange-300 dark:border-orange-600 text-orange-900 dark:text-orange-100 item-hours-input"
                    aria-label="Item hours"
                    :title="'Default: ' + item.service.defaultHours"
                  />
                  <button 
                    v-if="item.service.hours !== item.service.defaultHours"
                    @click="resetHours(getOriginalIndex(item))"
                    class="btn btn-icon text-orange-500 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-300"
                    title="Restaurar horas predeterminadas"
                  >
                    <i class="material-icons">restart_alt</i>
                  </button>
                </div>
              </div>

              <!-- Regular Toggle -->
              <div class="flex items-center gap-2">
                <button 
                  @click="toggleSupervisionOnly(getOriginalIndex(item))"
                  class="btn btn-sm bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-600 transition-colors"
                  title="Marcar como item regular"
                >
                  <span class="w-1.5 h-1.5 rounded-full mr-1 bg-slate-400"></span>
                  <span class="hidden sm:inline text-xs">Regular</span>
                </button>
              </div>

              <!-- Remove Button -->
              <div class="flex items-center gap-2">
                <button @click="confirmRemove(getOriginalIndex(item))" 
                        class="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 font-medium hover:underline focus:outline-none flex items-center gap-1"
                        :title="'Remove ' + item.service.name">
                  <i class="material-icons-round text-sm">delete</i>
                  <span>Eliminar</span>
                </button>
              </div>
            </div>
          </div>
        </template>
      </draggable>
    </div>

    <!-- 4.5️⃣ Subtotal de Items Solo Supervisión -->
    <div v-if="supervisionOnlyHours > 0" 
         class="flex items-center justify-between py-2 px-3 rounded-md bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700">
      <p class="text-orange-700 dark:text-orange-300 text-sm font-medium">Subtotal de Items Solo Supervisión</p>
      <p class="text-orange-700 dark:text-orange-300 text-sm font-medium">{{ supervisionOnlyHours }} horas</p>
    </div>

    <!-- 5️⃣ Total Supervision Hours (calculated + supervision-only) -->
    <div v-if="totalSupervisionHours > 0" 
         class="flex items-center justify-between py-2 px-3 rounded-md bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700">
      <p class="text-orange-700 dark:text-orange-300 text-sm font-medium">Total de Horas de Supervisión</p>
      <p class="text-orange-700 dark:text-orange-300 text-sm font-medium">{{ totalSupervisionHours }} horas</p>
    </div>

    <!-- 6️⃣ Total Hours Overall - Emphasized -->
    <div class="flex items-center justify-between py-2 px-3 rounded-md bg-orange-100 dark:bg-orange-900/50 border border-orange-200 dark:border-orange-700">
      <p class="text-orange-700 dark:text-orange-300 text-sm font-semibold">Total con Supervisión</p>
      <p class="text-orange-700 dark:text-orange-300 text-sm font-bold">{{ totalHours }} horas</p>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import Swal from 'sweetalert2';
import draggable from 'vuedraggable';

/**
 * ItemTable Component
 * 
 * Displays and manages the list of services/items in the estimation.
 * Features:
 * - Display of items with quantities and total hours
 * - Editing quantities
 * - Removing items
 * - Shows creative supervision hours
 * - Calculates and displays total hours
 */
export default {
  name: 'ItemTable',
  components: {
    draggable,
  },
  
  props: {
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    creativeSupervisionHours: {
      type: Number,
      default: 0
    },
    totalItems: Number,
    totalHours: Number,
    baseHours: Number,
    supervisionOnlyHours: {
      type: Number,
      default: 0
    },
    totalSupervisionHours: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      editingIndex: null, // Tracks which item is being edited
    }
  },

  computed: {
    regularItems() {
      return this.items.filter(item => !item.supervisionOnly);
    },
    supervisionOnlyItems() {
      return this.items.filter(item => item.supervisionOnly);
    },
    draggableRegularItems: {
      get() {
        return this.regularItems;
      },
      set(value) {
        const newItems = [...value, ...this.supervisionOnlyItems];
        this.$emit('update:items', newItems);
      }
    },
    draggableSupervisionOnlyItems: {
      get() {
        return this.supervisionOnlyItems;
      },
      set(value) {
        const newItems = [...this.regularItems, ...value];
        this.$emit('update:items', newItems);
      }
    }
  },

  setup() {
    const toast = useToast();
    
    return { toast };
  },

  methods: {
    /**
     * Get team tag CSS classes based on team name
     * @param {string} teamName - The name of the team
     * @returns {string} CSS classes for the team tag
     */
    getTeamTagClass(teamName) {
      const teamClasses = {
        // Services.js team names
        'Diseño': 'team-tag-creative',
        'Cuentas': 'team-tag-cuentas', 
        'Desarrollo': 'team-tag-dev',
        // Teams.js team names  
        'Creative': 'team-tag-creative',
        'Dev': 'team-tag-dev',
        'C-Suite': 'team-tag-csuite',
        'Social': 'team-tag-social'
      };
      return teamClasses[teamName] || 'team-tag-default';
    },

    /**
     * Get the original index of an item in the full items array
     * @param {Object} item - The item to find
     * @returns {number} The original index in the items array
     */
    getOriginalIndex(item) {
      return this.items.findIndex(i => i === item);
    },

    /**
     * Toggle supervision-only status for an item
     * @param {number} index - The index of the item in the original items array
     */
    toggleSupervisionOnly(index) {
      this.$emit('toggle-supervision-only', index);
    },

    /**
     * Starts editing mode for an item
     * @param {number} index - The index of the item to edit
     */
    startEditing(index) {
      this.editingIndex = index;
      // Emit event to parent for potential modal or inline editing
      this.$emit('edit-item', index);
    },

    /**
     * Updates an item's quantity with validation and recalculates total
     * @param {Event} event - The input event
     * @param {number} index - The index of the item to update
     */
    updateItemWithValidation(event, index) {
      const item = this.items[index];
      // Ensure quantity is at least 1
      item.quantity = Math.max(1, Math.floor(parseFloat(event.target.value) || 1));
      // Update displayed value
      event.target.value = item.quantity;
      // Update total hours
      this.$emit("item-updated", index);
    },

    /**
     * Updates an item's hours and recalculates total
     * @param {number} index - The index of the item to update
     */
    updateItemHours(index) {
      this.$emit("item-hours-updated", index);
    },

    /**
     * Reset item hours to default value
     * @param {number} index - The index of the item to reset
     */
    resetHours(index) {
      const item = this.items[index];
      if (item.service.defaultHours !== undefined) {
        item.service.hours = item.service.defaultHours;
        this.updateItemHours(index);
        this.toast.success("Horas restauradas al valor predeterminado");
      }
    },

    /**
     * Confirms and handles item removal
     * @param {number} index - The index of the item to remove
     */
    async confirmRemove(index) {
      const itemName = this.items[index].service.name;
      
      const result = await Swal.fire({
        title: '¿Está seguro?',
        text: `¿Desea eliminar "${itemName}"?`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, eliminar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6B7280',
        reverseButtons: true
      });

      if (result.isConfirmed) {
        this.$emit('item-removed', index);
        this.toast.success(`"${itemName}" eliminado exitosamente`);
      }
    },
  }
};
</script>
<style scoped>
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
</style>